// Turns the composer's editable DOM into the two parts of an email: allowlisted
// HTML and a plain-text fallback. The server rebuilds the HTML again from its
// own allowlist, so this is for a faithful message, not the security boundary.

export type EmailContent = { html: string; text: string }

const BLOCKS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI'])

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/**
 * The link a user typed, as a safe absolute URL, or null. Accepts http(s) and
 * mailto; a bare domain becomes https and a bare address becomes mailto.
 */
export function normalizeLinkInput(input: string): string | null {
  const value = input.trim()
  if (!value || /\s/.test(value)) return null

  let candidate = value
  if (/^[^@/:]+@[^@/:]+\.[^@/:]+$/.test(value)) candidate = `mailto:${value}`
  else if (!/^[a-z][a-z0-9+.-]*:/i.test(value)) candidate = `https://${value}`

  try {
    const url = new URL(candidate)
    if ((url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.')) return url.href
    if (url.protocol === 'mailto:' && /^mailto:[^@\s?]+@[^@\s?]+$/i.test(candidate)) return candidate
  } catch {
    return null
  }
  return null
}

function inline(node: Node, html: string[], text: string[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const value = (node.textContent ?? '').replace(/\u00a0/g, ' ')
    html.push(escapeHtml(value))
    text.push(value)
    return
  }
  if (!(node instanceof HTMLElement)) return

  const tag = node.tagName
  if (tag === 'BR') {
    html.push('<br>')
    text.push('\n')
    return
  }

  const children = (h: string[], t: string[]) => node.childNodes.forEach((child) => inline(child, h, t))

  if (tag === 'B' || tag === 'STRONG' || tag === 'I' || tag === 'EM') {
    const out = tag === 'B' || tag === 'STRONG' ? 'strong' : 'em'
    html.push(`<${out}>`)
    children(html, text)
    html.push(`</${out}>`)
    return
  }

  if (tag === 'A') {
    const href = normalizeLinkInput(node.getAttribute('href') ?? '')
    const label: string[] = []
    const labelHtml: string[] = []
    children(labelHtml, label)
    const shown = label.join('')
    if (!href) {
      html.push(...labelHtml)
      text.push(shown)
      return
    }
    html.push(`<a href="${escapeHtml(href)}">`, ...labelHtml, '</a>')
    const plainHref = href.replace(/^mailto:/, '')
    text.push(shown.trim() && shown.trim() !== plainHref && shown.trim() !== href ? `${shown} (${plainHref})` : shown || plainHref)
    return
  }

  // Spans, fonts and anything else: keep the content, drop the wrapper.
  children(html, text)
}

function trimTrailingBreak(html: string): string {
  // A block's closing <br> is only the editor keeping an empty line tall.
  return html.replace(/(?:<br>)+$/, '')
}

function list(node: HTMLElement, html: string[], text: string[]): void {
  const ordered = node.tagName === 'OL'
  const items: string[] = []
  let index = 0
  node.childNodes.forEach((child) => {
    if (!(child instanceof HTMLElement) || child.tagName !== 'LI') return
    index += 1
    const h: string[] = []
    const t: string[] = []
    child.childNodes.forEach((grand) => {
      if (grand instanceof HTMLElement && (grand.tagName === 'UL' || grand.tagName === 'OL')) {
        const nestedText: string[] = []
        list(grand, h, nestedText)
        t.push('\n', nestedText.join('').replace(/^/gm, '  ').trimEnd())
      } else {
        inline(grand, h, t)
      }
    })
    const itemText = t.join('').trim()
    // An empty bullet is the editor's leftover, not content.
    if (!itemText) {
      index -= 1
      return
    }
    items.push(`<li>${trimTrailingBreak(h.join(''))}</li>`)
    text.push(`${ordered ? `${index}.` : '-'} ${itemText}\n`)
  })
  if (items.length) html.push(ordered ? '<ol>' : '<ul>', ...items, ordered ? '</ol>' : '</ul>')
}

/** Serializes the editor root. Empty lines and loose inline content become paragraphs. */
export function serializeEmail(root: HTMLElement): EmailContent {
  const html: string[] = []
  const text: string[] = []
  let loose: Node[] = []

  const paragraph = (nodes: Node[], tag: 'p' | 'h1' = 'p') => {
    const h: string[] = []
    const t: string[] = []
    nodes.forEach((node) => inline(node, h, t))
    const body = trimTrailingBreak(h.join(''))
    html.push(`<${tag}>${body || '<br>'}</${tag}>`)
    text.push(`${t.join('').replace(/\n$/, '')}\n\n`)
  }

  const flush = () => {
    if (loose.length) paragraph(loose)
    loose = []
  }

  // Chrome's editing can nest blocks (a list inside a <p>), so a block that
  // holds other blocks is walked again instead of being flattened.
  const walk = (container: Node) => {
    container.childNodes.forEach((node) => {
      if (!(node instanceof HTMLElement) || !BLOCKS.has(node.tagName)) {
        loose.push(node)
        return
      }
      flush()
      if (node.tagName === 'UL' || node.tagName === 'OL') {
        list(node, html, text)
        text.push('\n')
      } else if ([...node.children].some((child) => BLOCKS.has(child.tagName))) {
        walk(node)
        flush()
      } else if (node.tagName === 'LI') {
        paragraph([...node.childNodes])
      } else {
        paragraph([...node.childNodes], node.tagName.startsWith('H') ? 'h1' : 'p')
      }
    })
  }

  walk(root)
  flush()

  const plain = text.join('').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
  // Drop leading and trailing empty paragraphs so blank lines are not sent.
  const markup = html.join('').replace(/^(?:<p><br><\/p>)+|(?:<p><br><\/p>)+$/g, '')
  return { html: plain ? markup : '', text: plain }
}
